import { useEffect, useState, useMemo } from 'react';
import config from '../../config/config';
import states from '../../utilities/states';
import BasicFormInput from './BasicFormInput';
import BasicFormSelect from './BasicFormSelect';
import BasicFormDateInput from './BasicFormDateInput';
import { makeValidDateBody } from '../../utilities/dates';
import { getAllContactsList } from '../../utilities/contacts';
import axios from 'axios';

import styles from '../styles/AddContactForm.module.css';
import { regionLeadOptions, electedOptions } from '../../data/contactForm';

function AddContactForm() {
  const [isReady, setIsReady] = useState(false);

  const [contactsListData, setContactsListData] = useState([]);
  const contactNames = useMemo(() => {
    const res = new Set();
    contactsListData.forEach(({ name }) => {
      if (!res.has(name)) {
        res.add(name);
      }
    });
    return res;
  }, [contactsListData]);

  const [name, setName] = useState('');
  const [region_lead, setRegionLead] = useState('');
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [ext, setExt] = useState('');

  const [affilName, setAffilName] = useState('');
  const [address_1, setAddress1] = useState('');
  const [address_2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('TX');
  const [zipcode, setZipcode] = useState('');

  const [startdate, setStartDate] = useState(new Date());
  const [enddate, setEndDate] = useState(null);
  const [term_enddate, setTermEndDate] = useState(null);
  const [elected, setElected] = useState(false);
  const [notes, setNotes] = useState('');

  const updateReference = {
    name: setName,
    region_lead: setRegionLead,
    title: setTitle,
    email: setEmail,
    phone: setPhone,
    ext: setExt,
    affiliation: setAffilName,
    address_1: setAddress1,
    address_2: setAddress2,
    city: setCity,
    state: setState,
    zipcode: setZipcode,
    startdate: setStartDate,
    enddate: setEndDate,
    term_enddate: setTermEndDate,
    elected: setElected,
    notes: setNotes
  };

  //For Autocomplete
  const [affiliation_communities, setAffiliationCommunities] = useState([]);

  async function createUser(contactData) {
    await axios
      .post(config.apiURL + `/contacts`, contactData, {
        headers: {
          'Content-Type': 'application/json',
          token: config.getToken()
        }
      })
      .then((res) => {
        alert('New Record Created');
        window.location.reload();
      });
  }

  useEffect(() => {
    async function getAllAffiliations() {
      await axios
        .get(config.apiURL + `/affiliations/list`, {
          headers: {
            'Content-Type': 'application/json',
            token: config.getToken()
          }
        })
        .then((response) => {
          const communitiesList = response.data.map((name) => ({
            label: name,
            value: name
          }));
          setAffiliationCommunities(communitiesList);
        });
    }
    async function getAllContacts() {
      const res = await getAllContactsList();
      if (res?.status === 200) {
        const { data } = res;
        setContactsListData(data);
      }
    }
    if (isReady === false) {
      getAllContacts();
      getAllAffiliations();
      setIsReady(true);
    }
  }, [isReady]);

  function mySubmitHandler(event) {
    event.preventDefault();
    // Post request data
    const postData = {
      name: name,
      region_lead: { String: region_lead, Valid: region_lead ? true : false },
      title: title,
      email: email,
      phone: phone,
      ext: ext,
      affiliation: affilName,
      address_1: address_1,
      address_2: address_2,
      city: city,
      state: state,
      zipcode: zipcode,
      elected: elected === 'true' ? true : false,
      startdate: makeValidDateBody(startdate),
      enddate: makeValidDateBody(enddate),
      term_enddate: makeValidDateBody(term_enddate),
      notes: { String: notes, Valid: notes ? true : false }
    };
    createUser(postData);
  }

  function handleDateChange(name, value) {
    const synthEvent = {
      target: { name, value }
    };
    myChangeHandler(synthEvent);
  }

  const myChangeHandler = (event) => {
    const { name, value } = event.target;
    updateReference[name](value);
  };

  return (
    <div className={styles.container}>
      <div className={`${styles.formBox}`}>
        {isReady ? (
          <form onSubmit={mySubmitHandler}>
            <h3 style={{ textDecoration: 'underline' }}>
              Complete the Contact Information and hit submit:
            </h3>
            {contactNames.has(name) && (
              <div className={`${styles.rowContainer} ${styles.error}`}>
                *Name already exist. Use upload record button to add a contact
                for this name.
              </div>
            )}
            <div className={styles.rowContainer}>
              <div className={`${styles.columnContainer} ${styles.col}`}>
                <BasicFormInput
                  label='*Person Name'
                  updateKey='name'
                  value={name}
                  onChange={myChangeHandler}
                />
                <BasicFormInput
                  label='*Title'
                  updateKey='title'
                  value={title}
                  onChange={myChangeHandler}
                />
                <BasicFormSelect
                  label='*Affiliation'
                  updateKey='affiliation'
                  value={affilName}
                  onChange={myChangeHandler}
                  options={affiliation_communities}
                  className={styles.select}
                  displayEmpty={true}
                />
                <BasicFormSelect
                  label='Region Lead'
                  updateKey='region_lead'
                  value={region_lead}
                  onChange={myChangeHandler}
                  options={regionLeadOptions}
                  displayEmpty={true}
                />
                <BasicFormSelect
                  label='Elected'
                  updateKey='elected'
                  value={elected}
                  onChange={myChangeHandler}
                  options={electedOptions}
                />
                <BasicFormDateInput
                  label='*Date Start'
                  updateKey='startdate'
                  value={startdate}
                  onChange={(val) => handleDateChange('startdate', val)}
                />
                <BasicFormDateInput
                  label='Date End'
                  updateKey='enddate'
                  value={enddate}
                  onChange={(val) => handleDateChange('enddate', val)}
                />
                <BasicFormDateInput
                  label='Term End Date'
                  updateKey='term_enddate'
                  value={term_enddate}
                  onChange={(val) => handleDateChange('term_enddate', val)}
                />
              </div>
              <div className={`${styles.columnContainer} ${styles.col}`}>
                <BasicFormInput
                  label='Email Address'
                  updateKey='email'
                  value={email}
                  onChange={myChangeHandler}
                />
                <div className={styles.rowContainer}>
                  <BasicFormInput
                    label='Phone Number'
                    updateKey='phone'
                    value={phone}
                    onChange={myChangeHandler}
                  />
                  <BasicFormInput
                    label='Ext'
                    updateKey='ext'
                    value={ext}
                    onChange={myChangeHandler}
                  />
                </div>
                <BasicFormInput
                  label='Street Address'
                  updateKey='address_1'
                  value={address_1}
                  onChange={myChangeHandler}
                />
                <BasicFormInput
                  label='Street Address 2'
                  updateKey='address_2'
                  value={address_2}
                  onChange={myChangeHandler}
                />
                <BasicFormInput
                  label='City'
                  updateKey='city'
                  value={city}
                  onChange={myChangeHandler}
                />
                <BasicFormSelect
                  label='State'
                  updateKey='state'
                  value={state}
                  onChange={myChangeHandler}
                  options={states.map((st) => ({ label: st, value: st }))}
                />
                <BasicFormInput
                  label='Zipcode'
                  updateKey='zipcode'
                  value={zipcode}
                  onChange={myChangeHandler}
                />
              </div>
            </div>
            <div>
              <div>
                <label>Notes</label>
                <textarea
                  rows='3'
                  cols='20'
                  name='notes'
                  id='notes'
                  value={notes}
                  onChange={myChangeHandler}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div className={styles.reverseRowContainer}>
              <button className={styles.bttn} type='submit' id='addContactBtn'>
                Submit
              </button>
            </div>
          </form>
        ) : (
          <p> ...Data Loading</p>
        )}
      </div>
    </div>
  );
}

export default AddContactForm;
